import { createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight, Check, Copy, MessageCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { copiarTexto } from "@/lib/clipboard";

// Keep the same field order and labelled plain-text format used by Azzuca.
// A single-field copy contains only its value, without labels or extra spaces.
const datos = [
  { id: "titular", label: "Titular", value: "Sonia Gonzalez" },
  { id: "rut", label: "RUT", value: "11893216-1", display: "11.893.216-1" },
  { id: "banco", label: "Banco", value: "BancoEstado" },
  {
    id: "tipo",
    label: "Tipo de cuenta",
    value: "Cuenta Vista",
    display: "CuentaRUT (Cuenta Vista)",
  },
  { id: "cuenta", label: "N.º de cuenta", value: "11893216" },
  { id: "correo", label: "Correo", value: "sonia.gonzalez.campos@hotmail.com" },
] as const;
const todosLosDatos = datos.map(({ label, value }) => `${label}: ${value}`).join("\n");
const comprobanteUrl = `https://wa.me/56920789478?text=${encodeURIComponent(
  "Hola, hice una transferencia a Minimarket Full Market. Te envío el comprobante de mi compra.",
)}`;
const canonical = "https://pymevisible.cl/transferencia-full-market";
const title = "Datos de transferencia | Minimarket Full Market";
const description =
  "Copia los datos de transferencia de Minimarket Full Market y envía tu comprobante por WhatsApp.";

export const Route = createFileRoute("/transferencia-full-market")({
  component: TransferenciaFullMarket,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { name: "robots", content: "noindex, nofollow, noarchive" },
      { name: "googlebot", content: "noindex, nofollow, noarchive" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:site_name", content: "Minimarket Full Market" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical },
      {
        property: "og:image",
        content: "https://pymevisible.cl/clientes/full-market/perfil.webp",
      },
      { property: "og:image:alt", content: "Full Market" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: canonical }],
  }),
});

function TransferenciaFullMarket() {
  const [copiado, setCopiado] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState("");
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiando = useRef(false);

  useEffect(
    () => () => {
      if (temporizador.current) clearTimeout(temporizador.current);
    },
    [],
  );

  async function copiar(id: string, texto: string, label: string) {
    if (copiando.current) return;
    copiando.current = true;
    if (temporizador.current) clearTimeout(temporizador.current);
    try {
      await copiarTexto(texto);
      setCopiado(id);
      setMensaje(id === "todos" ? "Todos los datos copiados." : `${label}: dato copiado.`);
      temporizador.current = setTimeout(() => setCopiado(null), 2500);
    } catch {
      setCopiado(null);
      setMensaje("No se pudo copiar. Mantén presionado el dato para copiarlo manualmente.");
    } finally {
      copiando.current = false;
    }
  }

  return (
    <main className="pv-page-hero fm-transferencia min-h-svh px-4 py-5 text-ink sm:px-6 sm:py-8 lg:grid lg:place-items-center lg:py-12">
      <div className="mx-auto w-full max-w-[520px] lg:max-w-[980px]">
        <div className="grid items-start gap-5 lg:grid-cols-[0.85fr_1.15fr] lg:gap-8">
          <header className="lg:sticky lg:top-8">
            <div className="flex items-center gap-4 lg:block">
              <img
                src="/clientes/full-market/perfil.webp"
                alt="Identidad de Full Market"
                width={512}
                height={512}
                loading="eager"
                decoding="async"
                className="fm-perfil size-[88px] shrink-0 rounded-2xl object-cover lg:size-[132px] lg:rounded-3xl"
              />
              <div className="min-w-0 lg:mt-6">
                <p className="font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-slate lg:text-xs">
                  Minimarket
                </p>
                <h1 className="mt-1 font-display text-[2.5rem] font-extrabold uppercase leading-[0.9] tracking-tight lg:mt-2 lg:text-[4.25rem]">
                  Full Market
                </h1>
                <p className="mt-2 text-xs leading-relaxed text-slate lg:mt-4 lg:max-w-[28ch] lg:text-sm">
                  Paga tu compra por transferencia.
                </p>
              </div>
            </div>

            <figure className="fm-local mt-7 hidden overflow-hidden rounded-3xl border border-ink/10 lg:block">
              <img
                src="/clientes/full-market/local-560.webp"
                srcSet="/clientes/full-market/local-560.webp 560w, /clientes/full-market/local-1120.webp 1120w"
                sizes="380px"
                alt="Fachada de Minimarket Full Market"
                width={560}
                height={420}
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full object-cover"
              />
              <figcaption className="bg-paper/80 px-4 py-3 text-sm font-medium text-slate">
                Gracias por preferirnos.
              </figcaption>
            </figure>
          </header>

          <section
            aria-labelledby="full-market-datos"
            className="pv-surface-card fm-datos relative overflow-hidden rounded-[1.75rem] p-5 sm:p-6"
          >
            <div aria-hidden="true" className="fm-acento absolute inset-x-0 top-0 h-1" />
            <h2
              id="full-market-datos"
              className="font-display text-[2rem] font-bold uppercase leading-none tracking-tight"
            >
              Datos de transferencia
            </h2>
            <p className="mt-2 text-[13px] leading-relaxed text-slate">
              Copia los datos y abre la app de tu banco.
            </p>

            <button
              type="button"
              onClick={() => copiar("todos", todosLosDatos, "Datos")}
              className="pv-button pv-button--primary mt-4 flex min-h-12 w-full items-center justify-center gap-2 px-4 py-3 text-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
            >
              {copiado === "todos" ? (
                <Check size={18} aria-hidden="true" />
              ) : (
                <Copy size={18} aria-hidden="true" />
              )}
              {copiado === "todos" ? "Datos copiados" : "Copiar todos los datos"}
            </button>

            <dl className="mt-3 divide-y divide-ink/10">
              {datos.map((dato) => (
                <div key={dato.id} className="flex items-center gap-2 py-2">
                  <div className="min-w-0 flex-1">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.1em] text-slate">
                      {dato.label}
                    </dt>
                    <dd
                      className={`mt-1 select-text break-words font-semibold leading-snug ${dato.id === "correo" ? "text-[13px] sm:text-sm" : "text-base"}`}
                    >
                      {"display" in dato ? dato.display : dato.value}
                    </dd>
                  </div>
                  <button
                    type="button"
                    onClick={() => copiar(dato.id, dato.value, dato.label)}
                    aria-label={`Copiar ${dato.label.toLowerCase()}`}
                    title={`Copiar ${dato.label.toLowerCase()}`}
                    className="fm-copiar flex size-11 shrink-0 items-center justify-center rounded-xl border border-ink/10 bg-paper text-primary transition-colors hover:border-primary/30 hover:bg-sky focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    {copiado === dato.id ? (
                      <Check size={18} aria-hidden="true" />
                    ) : (
                      <Copy size={18} aria-hidden="true" />
                    )}
                  </button>
                </div>
              ))}
            </dl>

            <p
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="mt-1 min-h-4 text-xs leading-4 text-primary"
            >
              {mensaje}
            </p>

            <div className="mt-2 rounded-xl border border-ink/10 bg-paper/70 px-3 py-2.5 text-xs leading-relaxed text-slate">
              <p>
                Si tu banco no muestra CuentaRUT, selecciona <strong>Cuenta Vista</strong>.
              </p>
              <p className="mt-1">
                Si no reconoce el bloque completo, copia cada dato con su botón.
              </p>
            </div>

            <div className="mt-4 border-t border-ink/10 pt-4">
              <a
                href={comprobanteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="pv-button pv-product-cta flex min-h-12 w-full items-center justify-center gap-2 px-3 py-3 text-center text-sm font-bold text-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
              >
                <MessageCircle size={19} className="shrink-0" aria-hidden="true" />
                Enviar comprobante por WhatsApp
                <ArrowUpRight size={16} className="hidden shrink-0 sm:block" aria-hidden="true" />
              </a>
              <p className="mt-2 text-center text-xs leading-relaxed text-slate">
                Adjunta la imagen o el archivo de tu comprobante.
              </p>
            </div>
          </section>
        </div>

        <footer className="mt-5 text-center lg:mt-7">
          <p className="mx-auto max-w-[50ch] text-xs leading-relaxed text-slate">
            Antes de transferir, verifica que el titular, el banco y el número de cuenta coincidan.
          </p>
          <a
            href="https://pymevisible.cl/"
            className="mt-2 inline-flex min-h-11 items-center rounded-lg px-2 font-mono text-[10px] uppercase tracking-[0.12em] text-slate underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            Hecho por Pyme Visible
          </a>
        </footer>
      </div>
    </main>
  );
}
